/**
 * Custom objects (K8, migrations/tenant/0083): a small per-company record kind
 * with its own fields, whose records belong to a contact. This file is the
 * single definition of what a field and a value may be. The repo validates
 * every write with it, and every read goes through `readValues`, which keeps
 * only the values that still fit the type's CURRENT fields: a removed field,
 * a removed select option or a stale value of another type is simply not
 * shown, so changing a type never breaks a stored record.
 */
import { z } from 'zod';

export const CUSTOM_FIELD_TYPES = ['text', 'number', 'date', 'boolean', 'select'] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const CUSTOM_OBJECT_LIMITS = {
  typesPerTenant: 10,
  fieldsPerType: 20,
  recordsPerContactPerType: 200,
  name: 60,
  slug: 40,
  fieldKey: 40,
  fieldLabel: 60,
  selectOptions: 20,
  optionLength: 60,
  textValue: 500,
  /** Bytes of the stored JSON of one record's values. */
  valuesJson: 8000,
  /** Characters of the stored JSON of one type's field list (the table CHECKs it). */
  fieldsJson: 20000,
  /** |number| at most this, so it round-trips through JSON and SQLite exactly. */
  numberAbs: 1e12,
  pageSize: 50,
} as const;

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000B-\u001F\u007F]/;

const SLUG_PATTERN = /^[a-z][a-z0-9-]*$/;
const KEY_PATTERN = /^[a-z][a-z0-9_]*$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function plain(max: number, label: string) {
  return z
    .string()
    .trim()
    .min(1, `${label} is required.`)
    .max(max, `${label} can be at most ${max} characters.`)
    .refine((s) => !CONTROL_CHARS.test(s), `${label} contains characters that are not allowed.`);
}

export const customObjectNameSchema = plain(CUSTOM_OBJECT_LIMITS.name, 'The name');

export const customObjectSlugSchema = z
  .string()
  .trim()
  .min(1, 'The slug is required.')
  .max(
    CUSTOM_OBJECT_LIMITS.slug,
    `The slug can be at most ${CUSTOM_OBJECT_LIMITS.slug} characters.`,
  )
  .regex(SLUG_PATTERN, 'The slug may only use a-z, 0-9 and "-", and must start with a letter.');

export const customFieldSchema = z
  .object({
    key: z
      .string()
      .min(1, 'Every field needs a key.')
      .max(
        CUSTOM_OBJECT_LIMITS.fieldKey,
        `A field key can be at most ${CUSTOM_OBJECT_LIMITS.fieldKey} characters.`,
      )
      .regex(
        KEY_PATTERN,
        'A field key may only use a-z, 0-9 and "_", and must start with a letter.',
      ),
    label: plain(CUSTOM_OBJECT_LIMITS.fieldLabel, 'A field label'),
    type: z.enum(CUSTOM_FIELD_TYPES),
    required: z.boolean().optional(),
    options: z
      .array(plain(CUSTOM_OBJECT_LIMITS.optionLength, 'An option'))
      .max(
        CUSTOM_OBJECT_LIMITS.selectOptions,
        `A select field can have at most ${CUSTOM_OBJECT_LIMITS.selectOptions} options.`,
      )
      .optional(),
  })
  .strict()
  .superRefine((f, ctx) => {
    if (f.type === 'select') {
      const opts = f.options ?? [];
      if (opts.length === 0) {
        ctx.addIssue({ code: 'custom', message: `"${f.label}" needs at least one option.` });
      }
      if (new Set(opts).size !== opts.length) {
        ctx.addIssue({ code: 'custom', message: `"${f.label}" has the same option twice.` });
      }
    } else if (f.options !== undefined && f.options.length > 0) {
      ctx.addIssue({ code: 'custom', message: `Only a select field can have options.` });
    }
  })
  .transform((f) => ({
    key: f.key,
    label: f.label,
    type: f.type,
    required: f.required === true,
    ...(f.type === 'select' ? { options: f.options ?? [] } : {}),
  }));

export type CustomField = z.output<typeof customFieldSchema>;

export const customFieldsSchema = z
  .array(customFieldSchema)
  .max(
    CUSTOM_OBJECT_LIMITS.fieldsPerType,
    `A type can have at most ${CUSTOM_OBJECT_LIMITS.fieldsPerType} fields.`,
  )
  .superRefine((fields, ctx) => {
    const seen = new Set<string>();
    for (const f of fields) {
      if (seen.has(f.key)) {
        ctx.addIssue({ code: 'custom', message: `The field key "${f.key}" is used twice.` });
      }
      seen.add(f.key);
    }
    if (JSON.stringify(fields).length > CUSTOM_OBJECT_LIMITS.fieldsJson) {
      ctx.addIssue({
        code: 'custom',
        message: 'These fields are too large together. Use fewer or shorter options and labels.',
      });
    }
  });

/** True for a real calendar date written as YYYY-MM-DD. */
export function isIsoDate(s: string): boolean {
  if (!DATE_PATTERN.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

export type CustomValue = string | number | boolean;

/** The zod schema for one value of `field` (never null: a missing value is left out). */
export function valueSchema(field: CustomField): z.ZodType<CustomValue> {
  switch (field.type) {
    case 'text':
      return plain(CUSTOM_OBJECT_LIMITS.textValue, `"${field.label}"`);
    case 'number':
      return z
        .number({ message: `"${field.label}" must be a number.` })
        .refine(
          (n) => Math.abs(n) <= CUSTOM_OBJECT_LIMITS.numberAbs,
          `"${field.label}" is too large.`,
        );
    case 'date':
      return z
        .string({ message: `"${field.label}" must be a date.` })
        .refine(isIsoDate, `"${field.label}" must be a date (YYYY-MM-DD).`);
    case 'boolean':
      return z.boolean({ message: `"${field.label}" must be yes or no.` });
    case 'select': {
      const options = field.options ?? [];
      return z
        .string({ message: `"${field.label}" must be one of its options.` })
        .refine((v) => options.includes(v), `"${field.label}" must be one of its options.`);
    }
  }
}

export type CustomValues = Record<string, CustomValue>;

export type ValidateValuesResult =
  { ok: true; values: CustomValues } | { ok: false; message: string };

/**
 * Validates the values of a record being written. Unknown keys are refused
 * (they would be dropped on read anyway, so silently storing them would only
 * hide a client bug). `null`, `undefined` and an empty string mean "no value";
 * a required field must have one.
 */
export function validateValues(
  fields: readonly CustomField[],
  input: unknown,
): ValidateValuesResult {
  if (input === null || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, message: 'The values must be an object.' };
  }
  const raw = input as Record<string, unknown>;
  const byKey = new Map(fields.map((f) => [f.key, f]));
  for (const key of Object.keys(raw)) {
    if (!byKey.has(key)) return { ok: false, message: `"${key}" is not a field of this type.` };
  }
  const values: CustomValues = {};
  for (const field of fields) {
    const v = raw[field.key];
    if (v === undefined || v === null || v === '') {
      if (field.required) return { ok: false, message: `"${field.label}" is required.` };
      continue;
    }
    const parsed = valueSchema(field).safeParse(v);
    if (!parsed.success) {
      return { ok: false, message: parsed.error.issues[0]?.message ?? 'Invalid value.' };
    }
    values[field.key] = parsed.data;
  }
  if (new TextEncoder().encode(JSON.stringify(values)).length > CUSTOM_OBJECT_LIMITS.valuesJson) {
    return { ok: false, message: 'The values are too large.' };
  }
  return { ok: true, values };
}

/**
 * Parses a stored `fields_json`. A broken row (never written by this code)
 * reads as no fields rather than failing the whole page.
 */
export function parseStoredFields(json: string): CustomField[] {
  try {
    const parsed = customFieldsSchema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

/**
 * The values of a stored record as the current fields see them: values of
 * removed fields, removed options or a stale type are left out.
 */
export function readValues(fields: readonly CustomField[], json: string): CustomValues {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return {};
  }
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: CustomValues = {};
  for (const field of fields) {
    const v = (raw as Record<string, unknown>)[field.key];
    if (v === undefined || v === null) continue;
    const parsed = valueSchema(field).safeParse(v);
    if (parsed.success) out[field.key] = parsed.data;
  }
  return out;
}

/**
 * Keys whose type differs between `before` and `after` (a field kept under
 * the same key but turned from e.g. text into number). The repo refuses such
 * a change while the type has records.
 */
export function changedFieldTypes(
  before: readonly CustomField[],
  after: readonly CustomField[],
): string[] {
  const old = new Map(before.map((f) => [f.key, f.type]));
  return after.filter((f) => old.has(f.key) && old.get(f.key) !== f.type).map((f) => f.key);
}
